import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FAQS } from "@/lib/data";
import { fadeUp, inViewOnce } from "@/lib/motion";
import { Plus } from "lucide-react";
import { track } from "@/lib/track";

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-40 border-t border-[color:var(--line)] bg-[color:var(--bone-2)]"
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        <div className="flex items-center justify-between mb-14 md:mb-20">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
            Ch. 07 — Questions
          </span>
          <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-3)]">
            §007 / 010
          </span>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={inViewOnce}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-4 font-serif text-[38px] md:text-[56px] leading-[0.98] tracking-[-0.03em] font-medium text-balance"
          >
            Frequently{" "}
            <em className="italic font-normal text-[color:var(--copper-2)]">
              asked.
            </em>
          </motion.h2>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={inViewOnce}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.05 } },
            }}
            className="col-span-12 md:col-span-8 border-t border-[color:var(--line-strong)]"
          >
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <motion.li
                  key={f.q}
                  variants={fadeUp}
                  className="border-b border-[color:var(--line-strong)]"
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => {
                      setOpen(isOpen ? -1 : i);
                      track("faq_toggle", { index: i, question: f.q });
                    }}
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${i}`}
                    className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <div className="flex items-baseline gap-5">
                      <span className="font-mono text-[10px] tabular-nums text-[color:var(--copper)]">
                        0{i + 1}
                      </span>
                      <span className="font-serif text-[20px] md:text-[26px] leading-[1.25] font-medium text-[color:var(--ink)]">
                        {f.q}
                      </span>
                    </div>
                    <span
                      className={`shrink-0 w-9 h-9 rounded-full border border-[color:var(--line-strong)] grid place-items-center transition-transform duration-500 ${isOpen ? "rotate-45 bg-[color:var(--ink)] text-[color:var(--bone)] border-[color:var(--ink)]" : "text-[color:var(--ink)]"}`}
                    >
                      <Plus size={14} strokeWidth={2} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={`faq-panel-${i}`}
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p
                          className="pb-6 md:pb-8 pl-10 md:pl-11 pr-16 max-w-[720px] text-[15px] md:text-[16px] leading-[1.65] text-[color:var(--ink-2)]"
                          data-testid={`faq-answer-${i}`}
                        >
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              );
            })}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}
